import React from 'react';
import PropTypes from 'prop-types';
import { useUI } from 'context/ui.context';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';

import { ReactComponent as ArrowRight } from 'assets/images/arrowRight.svg';
import { useMainLink } from 'hooks/useLocationPath';
import {
  isFaqPage,
  isMainPage,
  isApiKeysPage,
  isInnerTablePage,
  isInnerPricesTabPage,
  isInnerDeliveryTabPage,
  isInnerProductSettings,
} from 'utils/pages';
import { useFilterReducer } from 'context/filter/filter.context.reducer';
import { useAnalyticType } from 'context/analytic.type.context';
import {
  PAGE_API_KEYS,
  PAGE_INNER_PRODUCTS,
  PAGE_INNER_PRICES_TAB,
  PAGE_INNER_DELIVERY_TAB,
  PAGE_ADVERTISING_RATES,
  PAGE_POSITIONS,
  PAGE_SEO_COMPARE,
  PAGE_SEO_MONITORING,
  PAGE_SEO_BY_PHRASE,
} from 'constants';
import {
  BY_PHRASES,
  BY_PRODUCTS,
  PAGE_SEO_HINTS,
  PAGE_SEO_SMART,
  PAGE_SEO_SMART_DESCRIPTION,
  PAGE_INNER_PRODUCTS_SETTINGS,
} from '../../constants';
import SmartIcon from '../../assets/icons/SmartIcon';

const NavigationLink = ({ label, to, isActive, className }) => {
  const { isMobile } = useUI();
  const { setFilterDefault } = useFilterReducer();
  const history = useHistory();

  const handleFilterUpdate = () => {
    //при переходе между страницами обнулять некоторые параметры фильтра
    setFilterDefault({ nextLink: to, prevLink: window.location.pathname });

    history.push(to);
  };
  const isFavorite = to === PAGE_SEO_SMART;
  return (
    <a href={to} className="block h-full" onClick={(e) => e.preventDefault()}>
      <button
        onClick={handleFilterUpdate}
        className={cn(
          'h-full flex justify-between pb-4 border-b-[1px] lg:pb-0 lg:border-b-0 whitespace-nowrap mx-4 lg:mx-0',
          {
            ['border-green border-t-2 text-green']: isActive,
            ['text-gray-500']: !isActive,
            ['lg:my-0 lg:px-2']: !className,
            ['items-center']: !isFavorite,
            ['pt-[9px]']: isFavorite,
          },
          className,
        )}
      >
        <div>
          {isFavorite && (
            <div className="flex justify-center">
              <SmartIcon width={21} height={13} />
            </div>
          )}
          {label}
        </div>
        {isMobile && <ArrowRight className="stroke-gray-400" />}
      </button>
    </a>
  );
};

NavigationLink.propTypes = {
  label: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  route: PropTypes.string,
  isActive: PropTypes.bool,
  before: PropTypes.func,
  className: PropTypes.string,
};

const MobileLinks = ({ links }) => {
  const { setFilterDefault } = useFilterReducer();
  const history = useHistory();

  const handleFilterUpdate = (to) => {
    //при переходе между страницами обнулять некоторые параметры фильтра
    setFilterDefault({ nextLink: to, prevLink: window.location.pathname });

    history.push(to);
  };

  return (
    <div className="flex flex-col space-y-2">
      {links.map((link) => {
        const { label, to, className } = link;
        return (
          <a href={to} className="block w-full" onClick={(e) => e.preventDefault()} key={to}>
            <button
              onClick={() => handleFilterUpdate(to)}
              className={cn('flex justify-between text-[#333D37] items-center w-full', className)}
            >
              <span className="block text-sm leading-[22px]">{label}</span>
              <ArrowRight className="stroke-gray-400" />
            </button>
          </a>
        );
      })}
    </div>
  );
};

MobileLinks.propTypes = {
  links: PropTypes.any,
};

const MobileLinksBlock = ({ header, links, border }) => {
  return (
    <div className={cn('px-4 py-3', { 'border-t border-b border-[#E7EDF0]': border })}>
      <span className="block text-sm leading-[22px] font-bold mb-3">{header}</span>
      <div className="pl-4">
        <MobileLinks links={links} />
      </div>
    </div>
  );
};

MobileLinksBlock.propTypes = {
  header: PropTypes.string,
  links: PropTypes.any,
  border: PropTypes.bool,
};

const getSeoLinks = (links) => {
  if (window.location.hostname === 'go.wecheck.ru') {
    return links;
  }
  links.push({
    label: 'Умное Описание',
    to: PAGE_SEO_SMART_DESCRIPTION,
    id: 'page-seo-smart-description',
    isActive: window.location.pathname === PAGE_SEO_SMART_DESCRIPTION,
  });
  return links;
};

const Navigation = () => {
  const { isInner, analyticType } = useAnalyticType();
  const { isMobile } = useUI();

  const SEO_LINKS = [
    {
      label: 'Подбор Слов',
      to: `/seo/${BY_PRODUCTS}`,
      id: 'page-seo-words',
      isActive:
        window.location.pathname === `/seo/${BY_PRODUCTS}` ||
        window.location.pathname === `/seo/${BY_PHRASES}` ||
        window.location.pathname.includes(PAGE_SEO_HINTS),
    },
    {
      label: 'Сравнение',
      to: PAGE_SEO_COMPARE,
      id: 'page-seo-compare',
      isActive: window.location.pathname === PAGE_SEO_COMPARE,
    },
    {
      label: 'Мониторинг',
      to: PAGE_SEO_MONITORING,
      id: 'page-seo-monitoring',
      isActive: window.location.pathname === PAGE_SEO_MONITORING,
    },

    {
      label: 'Позиции Товаров',
      to: PAGE_SEO_BY_PHRASE,
      id: 'page-seo-by-phrase',
      isActive: window.location.pathname === PAGE_SEO_BY_PHRASE,
    },
    {
      label: 'Умный Подбор',
      to: PAGE_SEO_SMART,
      id: 'page-seo-smart',
      isActive: window.location.pathname === PAGE_SEO_SMART,
    },
    // {
    //   label: 'Подсказки',
    //   to: PAGE_SEO_HINTS,
    //   id: 'page-seo-hints',
    //   isActive: window.location.pathname === PAGE_SEO_HINTS,
    // },
  ];

  const OUTER_LINKS = [
    {
      label: 'Аналитика',
      to: useMainLink(),
      id: 'link-main',
      isActive: isMainPage(),
    },
    {
      label: 'Faq',
      to: '/faq',
      id: 'link-faq',
      isActive: isFaqPage(),
    },
  ];

  const INNER_LINKS = [
    {
      label: 'Аналитика',
      to: PAGE_INNER_PRODUCTS,
      id: 'page-inner-sales',
      isActive:
        isInnerTablePage() &&
        !isInnerPricesTabPage() &&
        !isInnerDeliveryTabPage() &&
        !isInnerProductSettings(),
    },
    {
      label: 'Расчет поставки',
      to: PAGE_INNER_DELIVERY_TAB,
      id: 'page-inner-delivery',
      isActive: isInnerDeliveryTabPage(),
    },
    {
      label: 'Цены',
      to: PAGE_INNER_PRICES_TAB,
      id: 'page-inner-prices',
      isActive: isInnerPricesTabPage(),
    },
    {
      label: 'Себестоимость',
      to: PAGE_INNER_PRODUCTS_SETTINGS,
      id: 'page-inner-products-settings',
      isActive: isInnerProductSettings(),
    },
    {
      label: 'API-ключи',
      to: PAGE_API_KEYS,
      id: 'link-api-keys',
      isActive: isApiKeysPage(),
    },
  ];

  const EXT_LINKS = [
    {
      label: 'Рекламные ставки',
      to: PAGE_ADVERTISING_RATES,
      id: 'link-advertising',
    },
    {
      id: 'link-seo',
      label: 'SEO продвижение',
      to: `/seo/${BY_PRODUCTS}`,
    },
    {
      id: 'link-positions',
      label: 'Позиции и доли',
      to: PAGE_POSITIONS,
    },
  ];
  if (isMobile) {
    return (
      <div>
        <MobileLinksBlock header={'Внешняя аналитика'} links={OUTER_LINKS} />
        <MobileLinksBlock header={'Внутренняя аналитика'} links={INNER_LINKS} border />
        <div className="py-3 px-4 border-b border-[#E7EDF0]">
          <MobileLinks links={EXT_LINKS} />
        </div>
      </div>
    );
  }
  return (
    <nav
      className="flex flex-col lg:flex-row lg:justify-between lg:max-w-lg lg:ml-4 lg:mr-4
    before:content-[''] before:my-4 before:border-l before:border-gray-230 before:pr-3"
    >
      {(isInner
        ? INNER_LINKS
        : analyticType === 'outer'
        ? OUTER_LINKS
        : analyticType === 'seo'
        ? getSeoLinks(SEO_LINKS)
        : []
      ).map((link) => (
        <NavigationLink {...link} key={link.id} />
      ))}
    </nav>
  );
};

export default Navigation;
